import * as React from "react";
import {useState} from "react";
import {Box, Button, Collapse, Divider, IconButton, TextField, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import {dateToString} from "../../Utils/dateUtils";
import AddTeamTransitionModal from "../../Containers/Users/AddTeamTransitionModal";
import EditTeamTransitionModal from "../../Containers/Users/EditTeamTransitionModal";

// DD/MM/YYYY per the CRM Book of Standards.
const fmt = (date) => (date ? dateToString(new Date(date), "dd/MM/yyyy") : "");
const CURRENT_DOT_COLOR = "#4caf50"; // success green
const PREVIOUS_DOT_COLOR = "#2a77ea"; // link blue

/**
 * Team change history shown below the Team dropdown on the user profile.
 * Renders a read-only "Team since" field, a "History (N)" toggle, a collapsible
 * timeline of team segments, and a "+ Add transition manually" action.
 */
const TeamHistory = (props) => {
    const {teamHistory, teamsList, onAddTransition, onEditTransition} = props;
    const [expanded, setExpanded] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const history = (teamHistory && teamHistory.history) || [];
    const changesCount = (teamHistory && teamHistory.changes_count) || 0;
    const currentEntry = history.find((entry) => !entry.end_date);
    // Current team first, then previous teams newest-start first (null start last).
    const orderedHistory = [...history].sort((a, b) => {
        if (!a.end_date && b.end_date) return -1;
        if (a.end_date && !b.end_date) return 1;
        return (b.start_date || "").localeCompare(a.start_date || "");
    });

    const renderRange = (entry) => {
        if (!entry.end_date) {
            return `since ${fmt(entry.start_date)} · current team`;
        }
        if (!entry.start_date) {
            return `– ${fmt(entry.end_date)}`;
        }
        return `${fmt(entry.start_date)} – ${fmt(entry.end_date)}`;
    };

    return (
        <Box sx={{margin: "16px 0"}}>
            {/* Team since + History toggle */}
            <Box sx={{display: "flex", alignItems: "center", margin: "16px 0"}}>
                <Typography component="span" sx={{flex: 2, marginRight: "5px", color: "text.disabled"}}>
                    Team since
                </Typography>
                <Box sx={{flex: 7, display: "flex", alignItems: "center", gap: "12px"}}>
                    <TextField
                        size="small"
                        disabled
                        value={currentEntry ? fmt(currentEntry.start_date) : ""}
                        sx={{width: "160px"}}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        color="inherit"
                        startIcon={<HistoryIcon/>}
                        disabled={history.length === 0}
                        onClick={() => setExpanded((prev) => !prev)}
                    >
                        History ({changesCount})
                    </Button>
                </Box>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Divider sx={{marginBottom: "12px"}}/>
                <Typography variant="body2" sx={{color: "text.disabled", marginBottom: "8px"}}>
                    Team history for this user
                </Typography>

                <Box component="ul" sx={{listStyle: "none", paddingLeft: 0, margin: 0}}>
                    {orderedHistory.map((entry) => (
                        <Box
                            component="li"
                            key={entry.user_team_history_id}
                            sx={{display: "flex", alignItems: "flex-start", gap: "10px", padding: "6px 0"}}
                        >
                            <Box
                                sx={{
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    marginTop: "5px",
                                    flexShrink: 0,
                                    backgroundColor: !entry.end_date ? CURRENT_DOT_COLOR : PREVIOUS_DOT_COLOR,
                                }}
                            />
                            <Box sx={{flex: 1}}>
                                <Typography sx={{fontWeight: 700, lineHeight: 1.3}}>
                                    {entry.team_name || `Team #${entry.team_id}`}
                                </Typography>
                                <Typography variant="body2" sx={{color: "text.secondary"}}>
                                    {renderRange(entry)}
                                </Typography>
                            </Box>
                            <IconButton
                                size="small"
                                aria-label="Edit transition"
                                onClick={() => setEditingEntry(entry)}
                            >
                                <EditIcon fontSize="small"/>
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            </Collapse>

            <Box sx={{marginTop: "12px"}}>
                <Button
                    variant="outlined"
                    size="small"
                    color="inherit"
                    startIcon={<AddIcon/>}
                    onClick={() => setModalOpen(true)}
                >
                    Add transition manually
                </Button>
            </Box>

            {/* Mount only while open so the form always starts fresh (no stale values from a previous add). */}
            {modalOpen && (
                <AddTeamTransitionModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    teamsList={teamsList}
                    onSubmit={onAddTransition}
                />
            )}

            {editingEntry && (
                <EditTeamTransitionModal
                    open={Boolean(editingEntry)}
                    onClose={() => setEditingEntry(null)}
                    entry={editingEntry}
                    onSubmit={onEditTransition}
                />
            )}
        </Box>
    );
};

export default TeamHistory;
